import { joinPath } from './utils.js';

const DATA_ROOT = '/data';
const cache = new Map();

function validPath(path) {
  return /^[A-Za-z0-9_./-]+\.json$/.test(path) && !path.includes('..');
}

async function fetchJson(relativePath, { signal, fresh = false } = {}) {
  if (!validPath(relativePath)) throw new Error(`Invalid data path: ${relativePath}`);
  if (!fresh && cache.has(relativePath)) return cache.get(relativePath);
  const request = fetch(`${DATA_ROOT}/${relativePath}`, {
    signal,
    headers: { Accept: 'application/json' },
  }).then(async (response) => {
    if (!response.ok) throw new Error(`Unable to load ${relativePath} (${response.status}).`);
    return response.json();
  });
  cache.set(relativePath, request);
  try {
    return await request;
  } catch (error) {
    cache.delete(relativePath);
    throw error;
  }
}

export async function loadMaster(options) {
  const master = await fetchJson('master.json', options);
  if (master?.app?.id !== 'progressive-dse-vocab-app' || master?.themes?.length !== 7) {
    throw new Error('The master database is missing or incompatible.');
  }
  return master;
}

export async function loadHeadwordIndex(options) {
  const index = await fetchJson('headword-index.json', options);
  if (index?.schemaVersion !== '1.0.0' || !Array.isArray(index.entries)) {
    throw new Error('The headword search index is missing or incompatible.');
  }
  return index.entries;
}

function normaliseSearchTerm(value) {
  return String(value ?? '')
    .normalize('NFKC')
    .toLocaleLowerCase('en')
    .replace(/\s+/gu, ' ')
    .trim();
}

export function searchHeadwords(entries, query, limit = 10) {
  const term = normaliseSearchTerm(query);
  if (!term) return [];

  return (entries ?? [])
    .map((entry) => {
      const headword = normaliseSearchTerm(entry.displayText);
      const chinese = normaliseSearchTerm(entry.definitionZh);
      let rank = Number.POSITIVE_INFINITY;
      if (headword === term) rank = 0;
      else if (headword.startsWith(term)) rank = 1;
      else if (headword.split(/[\s/-]+/u).some((word) => word.startsWith(term))) rank = 2;
      else if (headword.includes(term)) rank = 3;
      else if (chinese.includes(term)) rank = 4;
      return { entry, rank };
    })
    .filter(({ rank }) => Number.isFinite(rank))
    .sort((left, right) => left.rank - right.rank
      || left.entry.displayText.localeCompare(right.entry.displayText, 'en'))
    .slice(0, Math.max(1, limit))
    .map(({ entry }) => entry);
}

export async function loadTheme(themeId, options) {
  const master = await loadMaster(options);
  const entry = master.themes.find((theme) => theme.id === themeId);
  if (!entry) throw new Error(`Unknown theme: ${themeId}`);
  const index = await fetchJson(entry.file, options);
  if (index?.theme?.id !== themeId) throw new Error(`Theme index mismatch for ${themeId}`);
  return { master, entry, index, indexPath: entry.file };
}

export async function loadSubtopic(themeId, subtopicId, options) {
  const bundle = await loadTheme(themeId, options);
  const route = bundle.index.subtopics.find((item) => item.id === subtopicId);
  if (!route) throw new Error(`Unknown subtopic: ${subtopicId}`);
  const contentPath = joinPath(bundle.indexPath, route.file);
  const content = await fetchJson(contentPath, options);
  if (content?.subtopic?.id !== subtopicId) throw new Error(`Subtopic data mismatch for ${subtopicId}`);

  let catalog = null;
  if (bundle.index.catalogFile) {
    const catalogPath = joinPath(bundle.indexPath, bundle.index.catalogFile);
    catalog = await fetchJson(catalogPath, options);
  }
  return { ...bundle, route, content, contentPath, catalog };
}

export function flattenHeadwords(content) {
  return ['beginner', 'intermediate', 'advanced'].flatMap((level) =>
    (content.levels?.[level]?.headwords ?? []).map((headword) => ({ ...headword, level })));
}

export function listeningTargets(content, catalog) {
  const byId = new Map((catalog?.targets ?? []).map((target) => [target.id, target]));
  return (content.targetIds ?? []).map((id) => byId.get(id)).filter(Boolean);
}

export function clearDataCache() {
  cache.clear();
}
